import { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/hooks/useCart";
import { useStoreUrl } from "@/hooks/useStoreUrl";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, X, ShoppingBag } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export default function MiniCart() {
  const { items, itemCount, total, removeItem } = useCart();
  const [pendingRemove, setPendingRemove] = useState<{ id: string; name: string } | null>(null);
  const [open, setOpen] = useState(false);
  const { storeUrl } = useStoreUrl();

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <ShoppingCart className="h-5 w-5" />
          {itemCount > 0 && (
            <Badge className="absolute -right-1 -top-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-[10px] bg-primary text-primary-foreground shadow-sm">
              {itemCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="p-3 border-b border-border">
          <h3 className="font-semibold text-foreground text-sm">Carrinho ({itemCount})</h3>
        </div>
        {items.length === 0 ? (
          <div className="p-6 text-center">
            <ShoppingBag className="mx-auto mb-2 h-10 w-10 text-muted-foreground/20" />
            <p className="text-sm text-muted-foreground">Seu carrinho está vazio</p>
          </div>
        ) : (
          <>
            <div className="max-h-64 overflow-y-auto divide-y divide-border">
              {items.slice(0, 5).map((item) => (
                <div key={item.id} className="flex items-center gap-3 p-3">
                  <div className="h-12 w-12 shrink-0 rounded-lg bg-muted/50 overflow-hidden flex items-center justify-center">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="h-full w-full object-cover" />
                    ) : (
                      <ShoppingBag className="h-5 w-5 text-muted-foreground/20" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground line-clamp-1">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{item.quantity}x R$ {item.price.toFixed(2)}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => setPendingRemove({ id: item.id, name: item.name })}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              {items.length > 5 && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  +{items.length - 5} ite{items.length - 5 > 1 ? "ns" : "m"}
                </p>
              )}
            </div>
            <div className="p-3 border-t border-border space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Total</span>
                <span className="text-sm font-bold text-primary">R$ {total.toFixed(2)}</span>
              </div>
              <Link to={storeUrl("/cart")} className="block" onClick={() => setOpen(false)}>
                <Button className="w-full gap-2" size="sm">
                  <ShoppingCart className="h-4 w-4" /> Ver Carrinho
                </Button>
              </Link>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>

      <AlertDialog open={!!pendingRemove} onOpenChange={(open) => !open && setPendingRemove(null)}>
        <AlertDialogContent className="max-w-[340px] rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover produto?</AlertDialogTitle>
            <AlertDialogDescription>
              Deseja remover <span className="font-medium text-foreground">{pendingRemove?.name}</span> do carrinho?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-2">
            <AlertDialogCancel className="flex-1 mt-0">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (pendingRemove) { removeItem(pendingRemove.id); setPendingRemove(null); } }}
              className="flex-1 bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
